/**
 * TeamSystem Impact - Compliance Path · Doppia Materialità drill-down
 *
 * Sintesi + le 5 macro-aree più materiali (il KPI conta tutte le macro-aree
 * materiali). La matrice completa si apre a parte (pagina dedicata), per non
 * appesantire il pannello.
 */

import { Box, Button, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CompliancePathData } from './useCompliancePath';

const Kpi = ({ value, label }: { value: number | string; label: string }) => (
  <Box>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

export const DmaDrill = ({ data }: { data: CompliancePathData['dma'] }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isEN = i18n.language?.startsWith('en');
  const topAreas = data.areas.slice(0, 5);
  const maxAreaCount = topAreas.reduce((m, a) => Math.max(m, a.count), 0) || 1;

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={4}><Kpi value={data.totalIros} label={t('dashboard.path.dmaTotal')} /></Grid>
        <Grid item xs={4}><Kpi value={data.materialIros} label={t('dashboard.path.dmaMaterial')} /></Grid>
        <Grid item xs={4}><Kpi value={data.areas.length} label={t('dashboard.path.dmaAreas')} /></Grid>
      </Grid>

      {data.areas.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>{t('dashboard.path.dmaTopAreas')}</Typography>
          <Stack spacing={1}>
            {topAreas.map((a) => (
              <Box key={a.id}>
                <Box display="flex" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" noWrap sx={{ mr: 1 }}>{isEN ? a.nameEN : a.name}</Typography>
                  <Typography variant="body2" fontWeight={600}>{a.count}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(a.count / maxAreaCount) * 100}
                  sx={{ height: 5, borderRadius: 3 }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/dma/materiality')}>
          {t('dashboard.path.dmaOpenMatrix')}
        </Button>
      </Box>
    </Stack>
  );
};
